import {
	ColumnModeTypeEnum,
	SuperGridColDef,
	createBuilder,
	GetConfigType,
	TransformRowsParams,
	flattenObject,
	SelectCellRenderer,
	DashboardFrame,
} from '@aleph/supergrid';
import {
	alephToast,
	NotificationToast,
	NotificationType,
	ExpandHeaderCellRenderer,
	IndexHeaderRenderer,
	NameHeaderRenderer,
	SelectCellEditor,
	translate,
	CustomHeader,
	NoValuePlaceholder,
} from '@aleph/shared-components';
import classNames from 'classnames';
import StatusCellRenderer from 'wb-spotify/app/routes/superGrid/cellRenderers/status';
import SpotifyNameCellRenderer from 'wb-spotify/app/routes/superGrid/cellRenderers/name/container';
import {
	getAdAccountCurrency,
	groupOnlyBreakdowns,
	SPOTIFY_CAMPAIGN_OBJECTIVE_ICONS,
	SPOTIFY_LEVELS_WITH_ID_FIELD,
	SpotifyBreakdowns,
	SpotifyColors,
	SpotifyLevel,
	SpotifyLevelId,
} from 'wb-spotify/app/routes/superGrid/utils';
import {
	GROUP_COLUMN_DEFINITIONS,
	GroupGenderOptions,
	GroupPlacementsOptions,
	GroupPlatformOptions,
} from 'wb-spotify/app/routes/superGrid/columnDefinitions/group';
import AD_COLUMN_DEFINITIONS from 'wb-spotify/app/routes/superGrid/columnDefinitions/ad';
import RedditCellRendererWrapper from 'wb-reddit/app/routes/superGrid/cellRenderers/wrapper';
import {
	adHeaderNames,
	campaignHeaderNames,
	groupHeaderNames,
} from 'wb-spotify/app/routes/superGrid/columnDefinitions/headerNames';
import IndexCellRenderer from 'wb-reddit/app/common/components/indexCell';
import { captureException } from '@sentry/react';
import { ApolloClient } from '@apollo/client';
import { visit, FieldNode, Kind } from 'graphql';
import { LEVEL_ID_FILTER } from 'wb-spotify/app/routes/superGrid/actionRow/filters/utils';
import { HeaderClassParams, SortModelItem, IServerSideGetRowsParams } from 'ag-grid-community';
import NameCellEditor from 'wb-reddit/src/app/common/components/autoNaming/autoNamingEditor';
import {
	AdAccountsLifetimeStatsSuperGridListDocument,
	AdAccountsSuperGridListDocument,
	AdLifetimeStatsSuperGridListDocument,
	AdOrderByColumn,
	AdSuperGridListDocument,
	CampaignLifetimeStatsSuperGridListDocument,
	CampaignObjective,
	CampaignSuperGridListDocument,
	GroupLifetimeStatsSuperGridListDocument,
	GroupLifetimeStatsSuperGridListQueryGroupTargets,
	GroupOrderByColumn,
	GroupSuperGridListDocument,
} from 'wb-spotify/graphql/generated';
import StatCellRenderer from 'wb-spotify/app/routes/superGrid/cellRenderers/stat';
import SettingsHeader from 'wb-reddit/app/routes/superGrid/cellRenderers/settingsHeader';
import SettingsCellRenderer from 'wb-spotify/app/routes/superGrid/cellRenderers/settings';
import AdAccountSettingsCellRenderer from 'wb-spotify/app/routes/superGrid/cellRenderers/adAccountSettings';
import { getSpotifyBreakdownQuery } from 'wb-spotify/app/routes/superGrid/view/utils';
import ClientSettingsHeaderRenderer from 'wb-spotify/app/routes/superGrid/cellRenderers/clientSettingsHeader';
import ClientSettingsCellRenderer from 'wb-spotify/app/routes/superGrid/cellRenderers/clientSettings';
import useAdAccountDashboardStore from 'auth/lib/useAdAccountDashboardStore';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import { editableOnLevel } from 'wb-spotify/app/routes/superGrid/helpers';
import { startCase, toLower } from 'lodash';
import MoneyCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/money';
import { GROUP_AGE_RANGE_OPTIONS } from 'wb-spotify/app/routes/superGrid/cellEditors/ageRangeCellEditor';

const injectFieldToQuery = ({
	baseQuery,
	nodeToInject,
	field,
}: {
	baseQuery: any;
	nodeToInject: string;
	field: string;
}) => {
	// exception when lifetimestats is on, cause structure is not a field
	if (field === 'structure') {
		return baseQuery;
	}

	const pathParts = nodeToInject.split('.');

	// Helper to recursively find and modify a field by path
	const findAndModifyField = (selections: readonly any[], remainingPath: string[]): any[] | null => {
		if (remainingPath.length === 0) {
			return null;
		}

		const [targetName, ...restPath] = remainingPath;
		const isLastPart = restPath.length === 0;
		let modified = false;

		const newSelections = selections.map((selection) => {
			if (selection.kind !== Kind.FIELD) {
				return selection;
			}

			const fieldNode = selection as FieldNode;
			const fieldName = fieldNode.alias?.value || fieldNode.name.value;

			if (fieldName === targetName) {
				if (isLastPart) {
					// This is the target field - modify it
					if (!fieldNode.selectionSet) {
						return fieldNode;
					}

					const existingSelections = fieldNode.selectionSet.selections || [];
					const existingFieldNames = new Set(
						existingSelections
							.filter((sel) => sel.kind === Kind.FIELD)
							.map((sel) => {
								const selField = sel as FieldNode;

								return selField.alias?.value || selField.name.value;
							}),
					);

					if (field && !existingFieldNames.has(field)) {
						modified = true;
						const newFieldSelections = [
							...existingSelections,
							{
								kind: Kind.FIELD as const,
								name: {
									kind:  Kind.NAME as const,
									value: field,
								},
							},
						];

						return {
							...fieldNode,
							selectionSet: {
								...fieldNode.selectionSet,
								selections: newFieldSelections,
							},
						};
					}

					return fieldNode;
				}

				if (fieldNode.selectionSet) {
					// Continue searching in nested selection set
					const modifiedSelections = findAndModifyField(fieldNode.selectionSet.selections, restPath);

					if (modifiedSelections) {
						modified = true;

						return {
							...fieldNode,
							selectionSet: {
								...fieldNode.selectionSet,
								selections: modifiedSelections,
							},
						};
					}
				}
			}

			return selection;
		});

		return modified ? newSelections : null;
	};

	// For simple paths (no nesting), use the original logic
	if (pathParts.length === 1) {
		return visit(baseQuery, {
			Field(fieldNode: FieldNode) {
				if (fieldNode.name.value === nodeToInject && fieldNode.selectionSet) {
					const existingSelections = fieldNode.selectionSet.selections || [];
					const existingFieldNames = new Set(
						existingSelections.filter((sel) => sel.kind === Kind.FIELD).map((sel) => (sel as FieldNode).name.value),
					);

					if (field && !existingFieldNames.has(field)) {
						const newSelections = [
							{
								kind: Kind.FIELD as const,
								name: {
									kind:  Kind.NAME as const,
									value: field,
								},
							},
						];

						return {
							...fieldNode,
							selectionSet: {
								...fieldNode.selectionSet,
								selections: [...existingSelections, ...newSelections],
							},
						};
					}
				}

				return undefined;
			},
		});
	}

	// For nested paths, recursively visit and modify selection sets
	return visit(baseQuery, {
		SelectionSet(selectionSet) {
			const modifiedSelections = findAndModifyField(selectionSet.selections, pathParts);

			if (modifiedSelections) {
				return {
					...selectionSet,
					selections: modifiedSelections,
				};
			}

			return undefined;
		},
	});
};

/**
 * Helper function to process group targets options with fallback labels
 */
const processGroupTargetsOptions = (
	values: any[] | null | undefined,
	options: Array<{ value: string; label: string }>,
	translationKey: string,
	fallbackTransform?: (value: string) => string,
) => values?.map((value) => {
	if (!value) {
		return null;
	}

	const foundOption = options.find((option) => option.value === value);

	return (
		foundOption ?? {
			label: translate(translationKey, {
				defaultValue: fallbackTransform ? fallbackTransform(value) : startCase(toLower(value)),
			}),
			value,
		}
	);
});

export function processGroupTargetsFromBackendForSupergrid(
	groupTargets: GroupLifetimeStatsSuperGridListQueryGroupTargets | null | undefined,
) {
	return {
		...groupTargets,
		groupTargetsGeoTargets: {
			...groupTargets?.groupTargetsGeoTargets,
			countryCode: groupTargets?.groupTargetsGeoTargets?.countryCode
				? {
					value:      groupTargets?.groupTargetsGeoTargets?.countryCode,
					label:      groupTargets?.groupTargetsGeoTargets?.geoName,
					__typename: 'Geo',
				}
				: undefined,
		},
		groupTargetsPlatforms: processGroupTargetsOptions(
			groupTargets?.groupTargetsPlatforms,
			GroupPlatformOptions,
			`sp: supergrid groupTargetsPlatforms.${groupTargets?.groupTargetsPlatforms?.[0] || ''}`,
		),
		groupTargetsGenders: GroupGenderOptions.find((option) => option.value === groupTargets?.groupTargetsGenders?.[0])
			?.value,
		groupTargetsPlacements: processGroupTargetsOptions(
			groupTargets?.groupTargetsPlacements,
			GroupPlacementsOptions,
			`sp: supergrid groupTargetsPlacements.${groupTargets?.groupTargetsPlacements?.[0] || ''}`,
		),
		groupTargetsPodcastEpisodeTopics: groupTargets?.groupTargetsPodcastEpisodeTopics?.map((topic) => ({
			value:      topic?.podcastEpisodeTopic?.value,
			label:      topic?.podcastEpisodeTopic?.label,
			__typename: topic?.podcastEpisodeTopic?.__typename ?? 'PodcastEpisodeTopic',
		})),
		groupTargetsGenres: groupTargets?.groupTargetsGenres?.map((genre) => ({
			value:      genre?.genre?.value,
			label:      genre?.genre?.label,
			__typename: genre?.genre?.__typename ?? 'Genre',
		})),
		groupTargetsInterests: groupTargets?.groupTargetsInterests?.map((interest) => ({
			value:      interest?.interest?.value,
			label:      interest?.interest?.label,
			__typename: interest?.interest?.__typename ?? 'Interest',
		})),
		groupTargetsArtists: groupTargets?.groupTargetsArtists?.map((artist) => ({
			value:      artist?.artist?.value,
			label:      artist?.artist?.label,
			__typename: artist?.artist?.__typename ?? 'Artist',
		})),
		groupTargetsAgeRanges: groupTargets?.groupTargetsAgeRanges?.map((ageRange) => {
			const foundOption = GROUP_AGE_RANGE_OPTIONS.find((option) => {
				const [min, max] = option.value.split('-').map(Number);

				return min === ageRange?.min && max === ageRange?.max;
			});

			return (
				foundOption ?? {
					label: `${ageRange?.min}-${ageRange?.max}`,
					value: `${ageRange?.min}-${ageRange?.max}`,
				}
			);
		}),
		groupTargetsSensitiveTopicExclusions: {
			...groupTargets?.groupTargetsSensitiveTopicExclusions,
			sensitiveTopics: groupTargets?.groupTargetsSensitiveTopicExclusions?.sensitiveTopics?.map((topic) => ({
				value:      topic?.sensitiveTopic?.sensitiveTopicId,
				label:      topic?.sensitiveTopic?.sensitiveTopicName,
				__typename: topic?.sensitiveTopic?.__typename ?? 'SensitiveTopic',
			})),
		},
		groupTargetsPlaylists: groupTargets?.groupTargetsPlaylists?.map((playlist) => ({
			value:      playlist?.playlist?.playlistId,
			label:      playlist?.playlist?.playlistName,
			__typename: playlist?.playlist?.__typename ?? 'Playlist',
		})),
	};
}

/**
 * Interface of which fields are added in the transformSpotifyRow function
 * Omitting the id since it comes from the query type
 */
export interface SpotifySupergridRow {
	hasErrors: boolean;
	isDashboardView: boolean;
	supergridIsDashboardView: boolean;
	level: SpotifyLevel;
	isLastRow: boolean;
	currency: string | null | undefined;
	isBreakdownParent: boolean;
}

function dedupeById<T extends { id: string }>(items: T[]): T[] {
	const seen = new Set<string>();
	const out: T[] = [];

	// eslint-disable-next-line no-restricted-syntax
	for (const it of items) {
		if (!seen.has(it.id)) {
			seen.add(it.id);
			out.push(it);
		}
	}

	return out;
}

/**
 * Recursively processes children and applies parent IDs at each level
 */
const processChildrenWithParentIds = (children: any[] | null | undefined, parentId: string): any[] | undefined => {
	if (!children) {
		return undefined;
	}

	return children.map((child) => ({
		...child,
		parent: {
			id: parentId,
		},
		children: processChildrenWithParentIds(child?.children, child.id),
		// We also need to process group targets for the children
		// This is used when publishing draft campaign.
		// When user publishes draft campaign also their groups are published
		...(child.groupTargets
			? {
				groupTargets: processGroupTargetsFromBackendForSupergrid(child?.groupTargets),
			}
			: {}),
	}));
};

const processSpotifyRow = (
	row: SpotifyRows[number],
	requestLevel: SpotifyLevel,
	breakdown: SpotifyBreakdowns = SpotifyBreakdowns.ENTITIES,
) => {
	if (row.supergridIsNoDataRow || row.supergridIsCreateRow) {
		return flattenObject({
			...row,
			level:     requestLevel,
			isLastRow: row.superGridIsLastRow,
		});
	}

	const newRow = {
		...row,
		hasErrors:                false,
		isDashboardView:          row.supergridLevel === SpotifyLevel.AD_ACCOUNT && row?.clientPinned,
		supergridIsDashboardView: row.supergridLevel === SpotifyLevel.AD_ACCOUNT && row?.clientPinned,
		level:                    requestLevel,
		isLastRow:                row.superGridIsLastRow,
		currency:                 getAdAccountCurrency(row),
		...(row.supergridLevel === SpotifyLevel.GROUP
			? {
				currency:     row?.groupBudget?.currency,
				groupTargets: processGroupTargetsFromBackendForSupergrid(row?.groupTargets),
			}
			: {}),
		isBreakdownParent: breakdown !== SpotifyBreakdowns.ENTITIES,
		...(row.supergridLevel !== SpotifyLevel.BREAKDOWN ? row.flatStats?.[0] : {}),
		// apply parent id to the children recursively
		children:          row.id && 'children' in row ? processChildrenWithParentIds(row.children, row.id) : undefined,
	};

	return flattenObject(newRow);
};

const appendTargetingLabels = async (rows, requestLevel: SpotifyLevel) => {
	if (requestLevel !== SpotifyLevel.GROUP) {
		return rows;
	}

	try {
		return rows.map((row) => ({
			...row,
		}));
	} catch (e) {
		captureException(e);

		alephToast(
			<NotificationToast
				type={NotificationType.Error}
				title="There was an error while getting targeting data."
				description="Some groups may have corrupt targeting settings."
			/>,
		);
	}

	return rows;
};

export const NAME_COL_DEF: SuperGridColDef = {
	field:                 'name',
	columnMode:            ColumnModeTypeEnum.BOTH,
	headerComponent:       NameHeaderRenderer,
	cellRenderer:          SpotifyNameCellRenderer,
	cellEditor:            NameCellEditor,
	lockPosition:          true,
	headerComponentParams: (params) => {
		const columns = params.columnApi?.getColumns() ?? [];

		return {
			showLabel: columns[columns.indexOf(params.column) - 1]?.colDef?.field !== params?.column?.colDef?.field,
		};
	},
	cellEditorParams: (params) => ({
		...params,
		rowHeight: params.node.rowHeight,
	}),
	// @ts-ignore
	headerClass: (params: HeaderClassParams) => {
		const columns = params.columnApi?.getColumns() ?? [];

		const { column } = params;

		return classNames('border-r-0 border-b border-b-gray-300 ', {
			'!overflow-visible border-l !w-auto':
        columns && columns[column ? columns.indexOf(column) - 1 : 0]?.getColDef()?.field !== column?.getColDef()?.field,
		});
	},
	cellStyle: {
		borderRightWidth: 1,
		borderLeftWidth:  1,
		borderRightColor: 'hsl(var(--gray-300))',
		borderLeftColor:  'hsl(var(--gray-300))',
		padding:          0,
	},
};

export const SPOTIFY_CAMPAIGN_OBJECTIVE_OPTIONS = Object.values(CampaignObjective).map((objective) => ({
	icon:  SPOTIFY_CAMPAIGN_OBJECTIVE_ICONS[objective],
	value: objective,
	label: translate(`sp: campaign objective ${objective}`, {
		defaultValue: startCase(objective.toLowerCase()),
	}),
}));

type GetSpotifyVariables = (useBreakdown: boolean) => (level: SpotifyLevel, filterModel: SortModelItem[]) => any;

const getSpotifyVariables = (
	level: SpotifyLevel,
	parentId: string | undefined | null,
	filterMode: SortModelItem[],
	dataSourceParams: IServerSideGetRowsParams,
	cursor: string | null | undefined,
	limit: number | null | undefined,
	getVariables?: GetSpotifyVariables,
	breakdown: SpotifyBreakdowns = SpotifyBreakdowns.ENTITIES,
) => {
	const useBreakdown = breakdown && breakdown !== SpotifyBreakdowns.ENTITIES && dataSourceParams?.parentNode?.level !== -1;
	const variables = getVariables?.(useBreakdown)(level, filterMode);

	const parentLevel = dataSourceParams?.parentNode?.data?.supergridLevel ?? undefined;
	const parentFilter = !parentLevel ? {} : { [LEVEL_ID_FILTER[parentLevel]]: [{ value: [parentId ?? ''] }] };

	return {
		...variables,
		filters: {
			...variables?.filters,
			...parentFilter,
		},
		cursor,
		limit: limit ?? 100,
	};
};

const getNoDataRow = (breakdown: SpotifyBreakdowns = SpotifyBreakdowns.ENTITIES) => (breakdown !== SpotifyBreakdowns.ENTITIES
	? {
		name:        'No data',
		isNoDataRow: true,
	}
	: null);

const getDefaultCreateData = (
	dataSourceParams: IServerSideGetRowsParams,
	readOnly?: boolean,
	breakdown: SpotifyBreakdowns = SpotifyBreakdowns.ENTITIES,
) => {
	if (breakdown !== SpotifyBreakdowns.ENTITIES || readOnly || !(dataSourceParams.parentNode.level >= 0)) {
		return null;
	}

	return {
		status:   'DRAFT',
		isCreate: true,
	};
};

export const CONFIG_BASE = createBuilder()
	.metaData<{ cursor?: string | null }>()
	.context<{
	client: ApolloClient<any>;
	breakdown?: SpotifyBreakdowns;
	readOnly?: boolean;
	getVariables?: GetSpotifyVariables;
}>()
	.addLevel({ name: SpotifyLevel.AD_ACCOUNT })
	.addLevel({ name: SpotifyLevel.CAMPAIGN })
	.addLevel({ name: SpotifyLevel.GROUP })
	.addLevel({ name: SpotifyLevel.AD })
	.addLevel({ name: SpotifyLevel.BREAKDOWN })
	.getDataForLevel(
		SpotifyLevel.AD_ACCOUNT,
		async ({
			ctx, dataSourceParams, level, parentId, filteredSortModel, previousRequestMetaData, limit,
		}) => {
			const { data } = await ctx.client.query({
				query: useSupergridSettings.getState().lifetimeStats
					? AdAccountsLifetimeStatsSuperGridListDocument
					: AdAccountsSuperGridListDocument,
				variables: getSpotifyVariables(
					level,
					parentId,
					filteredSortModel,
					dataSourceParams,
					previousRequestMetaData?.cursor,
					limit,
					ctx.getVariables,
					ctx.breakdown,
				),
				fetchPolicy: 'network-only',
			});

			const { adAccountSettings } = useAdAccountDashboardStore.getState();

			return {
				data: (data?.entities?.items ?? []).map((item) => ({
					...item.adAccount,
					id:              item.clientId,
					clientId:        item.clientId,
					clientPinned:    adAccountSettings[item.clientId]?.isPinned,
					clientKpi:       adAccountSettings[item.clientId]?.clientKpi,
					clientBreakdown: adAccountSettings[item.clientId]?.clientBreakdown,
				})),
				metaData:  { cursor: data.entities.cursor },
				noDataRow: getNoDataRow(ctx.breakdown),
			};
		},
	)
	.getDataForLevel(
		SpotifyLevel.CAMPAIGN,
		async ({
			ctx, dataSourceParams, level, parentId, filteredSortModel, previousRequestMetaData, limit,
		}) => {
			const { data } = await ctx.client.query({
				query: useSupergridSettings.getState().lifetimeStats
					? CampaignLifetimeStatsSuperGridListDocument
					: CampaignSuperGridListDocument,
				variables: getSpotifyVariables(
					level,
					parentId,
					filteredSortModel,
					// @ts-ignore
					dataSourceParams,
					previousRequestMetaData?.cursor,
					limit,
					ctx.getVariables,
					ctx.breakdown,
				),
				// Force fetching of lifetime stats
				fetchPolicy: 'network-only',
			});

			return {
				// TODO: this needs to be fixed on the BE, if using subquerys we get duplicates
				data:      dedupeById(data?.entities?.items ?? []),
				metaData:  { cursor: data?.entities?.cursor },
				noDataRow: getNoDataRow(ctx.breakdown),
				createRow: getDefaultCreateData(dataSourceParams, ctx.readOnly, ctx.breakdown),
			};
		},
	)
	.getDataForLevel(
		SpotifyLevel.GROUP,
		async ({
			ctx, dataSourceParams, level, parentId, filteredSortModel, previousRequestMetaData, limit,
		}) => {
			const { data } = await ctx.client.query({
				query:
					// @ts-ignore TODO fix later
					useSupergridSettings.getState().lifetimeStats || groupOnlyBreakdowns.includes(ctx.breakdown)
						? injectFieldToQuery({
							baseQuery:    GroupLifetimeStatsSuperGridListDocument,
							field:        ctx.breakdown as string,
							nodeToInject: 'items.flatStats',
						})
						: GroupSuperGridListDocument,
				variables: getSpotifyVariables(
					level,
					parentId,
					filteredSortModel,
					// @ts-ignore
					dataSourceParams,
					previousRequestMetaData?.cursor,
					limit,
					ctx.getVariables,
					ctx.breakdown,
				),
				// Force fetching of lifetime stats
				fetchPolicy: 'network-only',
			});

			return {
				// TODO: this needs to be fixed on the BE, if using subquerys we get duplicates
				data:      dedupeById(data?.entities?.items ?? []),
				metaData:  { cursor: data?.entities?.cursor },
				noDataRow: getNoDataRow(ctx.breakdown),
				createRow: getDefaultCreateData(dataSourceParams, ctx.readOnly, ctx.breakdown),
			};
		},
	)
	.getDataForLevel(
		SpotifyLevel.AD,
		async ({
			ctx, dataSourceParams, parentId, level, filteredSortModel, previousRequestMetaData, limit,
		}) => {
			const variables = getSpotifyVariables(
				level,
				parentId,
				filteredSortModel,
				// @ts-ignore
				dataSourceParams,
				previousRequestMetaData?.cursor,
				limit,
				ctx.getVariables,
				ctx.breakdown,
			);

			// Remove campaignId filter, not needed for ad level
			delete variables.filters.campaignId;

			const { data } = await ctx.client.query({
				query: useSupergridSettings.getState().lifetimeStats
					? AdLifetimeStatsSuperGridListDocument
					: AdSuperGridListDocument,
				variables,
				// Force fetching of lifetime stats
				fetchPolicy: 'network-only',
			});

			return {
				// TODO: this needs to be fixed on the BE, if using subquerys we get duplicates
				data:      dedupeById(data.entities.items ?? []),
				metaData:  { cursor: data.entities.cursor },
				noDataRow: getNoDataRow(ctx.breakdown),
				createRow: getDefaultCreateData(dataSourceParams, ctx.readOnly, ctx.breakdown),
			};
		},
	)
	.getDataForLevel(
		SpotifyLevel.BREAKDOWN,
		async ({
			ctx, dataSourceParams, level, parentId, filteredSortModel, previousRequestMetaData, limit,
		}) => {
			const variables = getSpotifyVariables(
				level,
				parentId,
				filteredSortModel,
				dataSourceParams,
				previousRequestMetaData?.cursor,
				limit,
				ctx.getVariables,
				ctx.breakdown,
			);

			if (
				ctx.breakdown !== SpotifyBreakdowns.DAILY
        && ctx.breakdown !== SpotifyBreakdowns.ENTITIES
        && ctx.breakdown !== SpotifyBreakdowns.HOURLY
			) {
				// Remove date filter
				variables.filters = {
					...variables.filters,
					date: undefined,
				};

				variables.statsFilter = {
					...variables.statsFilter,
					date: undefined,
				};
			}

			const baseQuery = getSpotifyBreakdownQuery(ctx.breakdown)!;

			// Transform the query to conditionally add fields
			const transformedQuery = SPOTIFY_LEVELS_WITH_ID_FIELD.includes(dataSourceParams.parentNode.data.level)
				? injectFieldToQuery({
					baseQuery,
					field:        SpotifyLevelId[dataSourceParams.parentNode.data.level],
					nodeToInject: 'items',
				})
				: baseQuery;

			const { data } = await ctx.client.query({
				query:       transformedQuery,
				variables,
				fetchPolicy: 'network-only',
			});

			return {
				data:      data?.entities?.items ?? [],
				metaData:  { cursor: data?.entities?.cursor },
				noDataRow: getNoDataRow(ctx.breakdown),
			};
		},
	);

const CONFIG_BASE_CONFIG = CONFIG_BASE.getConfig();

type ConfigBuilderType = GetConfigType<typeof CONFIG_BASE_CONFIG>;

export type SpotifyRows = TransformRowsParams<
ConfigBuilderType['levelNames'],
ConfigBuilderType['queriedData'],
ConfigBuilderType['context']
>['rows'];

export const transformSpotifyRows = async (
	rows: SpotifyRows,
	requestLevel: SpotifyLevel,
	breakdown: SpotifyBreakdowns = SpotifyBreakdowns.ENTITIES,
	// eslint-disable-next-line @typescript-eslint/return-await
) => await appendTargetingLabels(rows?.map((row) => processSpotifyRow(row, requestLevel, breakdown)) ?? [], requestLevel);

export const SPOTIFY_SUPERGRID_CONFIG = CONFIG_BASE.transformRows(async ({ ctx, rows, level }) => {
	const result = await transformSpotifyRows(rows, level, ctx.breakdown);

	return result;
})
	.options({
		showHiddenNameColumns:  true,
		emptyCellsCellRenderer: () => <NoValuePlaceholder />,
		cellRendererWrapper:    RedditCellRendererWrapper,
		expandSkipSpacing:      (params) => params.data?.level === SpotifyLevel.BREAKDOWN,
		expandColDef:           {
			lockPosition:       true,
			headerComponent:    ExpandHeaderCellRenderer,
			cellRendererParams: {
				onToggle: (e, params) => {
					if (
						params.node?.data?.isCreate
            || (params.node?.data?.level === SpotifyLevel.AD && !params.node?.data?.isBreakdownParent)
            || params.node?.data?.level === SpotifyLevel.BREAKDOWN
					) {
						return;
					}

					params.node.setExpanded(!params.node.expanded, e);
					params.api.refreshCells();
				},
				Component: (params) => {
					const isDashboardView = params.data?.supergridIsDashboardView && !params.node.expanded;
					const colors = params.context?.colors;
					const nodeLevelName = params.node?.data?.supergridLevel ?? '';

					return (
						<>
							{isDashboardView && (
								<div className="pt-[1px]">
									<DashboardFrame className={`border-l border-l-[${colors[nodeLevelName]}]`} />
								</div>
							)}
						</>
					);
				},
			},
			headerClass: 'border-r-0 border-b border-b-gray-300',
			cellStyle:   {
				borderRightColor:  'transparent',
				borderBottomColor: 'transparent',
				borderLeftColor:   'transparent',
				overflow:          'visible',
				borderRightWidth:  0,
				borderTopWidth:    0,
				borderBottomWidth: 0,
				borderLeftWidth:   0,
				boxShadow:         'none',
				padding:           0,
			},
		},
		globalLevel: {
			color:   'hsl(var(--gray-300))',
			anchors: {
				color:         'hsl(var(--gray-600))',
				firstColumnId: 'listeners',
				label:         'METRICS',
			},
			columns: [
				{
					field:           'index',
					cellRenderer:    IndexCellRenderer,
					headerComponent: IndexHeaderRenderer,
					headerName:      '#',
					sortable:        false,
					pinned:          true,
					lockPosition:    true,
					headerClass:     () => [
						'border-r-gray-300',
						'border-r-[1px]',
						'border-b-[1px]',
						'border-b-gray-300',
						'flex',
						'justify-center',
					],
					width:              36,
					minWidth:           36,
					maxWidth:           36,
					cellRendererParams: (params) => ({
						disableSelection: params.data?.level === SpotifyLevel.AD_ACCOUNT,
					}),
				},
				{
					colId:              'status',
					field:              'status',
					sortable:           true,
					cellRenderer:       StatusCellRenderer,
					headerComponent:    CustomHeader,
					cellRendererParams: {
						skipCellRendererWrapper: true,
					},
					pinned:          'left',
					lockPosition:    true,
					suppressMovable: true,
					headerName:      'Live',
					width:           48,
					minWidth:        48,
					maxWidth:        48,
				},
				{
					columnMode:           ColumnModeTypeEnum.REGULAR,
					field:                'clientPinnedSettings',
					headerComponent:      ClientSettingsHeaderRenderer,
					pinned:               'left',
					maxWidth:             36,
					minWidth:             36,
					cellRendererSelector: (params) => {
						if (params.node?.data?.level === SpotifyLevel.AD_ACCOUNT) {
							return {
								component: ClientSettingsCellRenderer,
								params:    {
									...params,
									columnOrderIndex: 1,
								},
							};
						}

						return {
							component: AdAccountSettingsCellRenderer,
							params:    {
								...params,
								columnOrderIndex: 1,
							},
						};
					},
				},
				{
					field:                'settings',
					pinned:               'left',
					elasticParent:        'settingsElastic',
					headerComponent:      SettingsHeader,
					minWidth:             36,
					maxWidth:             36,
					cellRendererSelector: (params) => {
						if ([SpotifyLevel.AD_ACCOUNT].includes(params.node?.data?.level)) {
							return {
								component: AdAccountSettingsCellRenderer,
								params:    {
									...params,
									columnOrderIndex: 2,
								},
							};
						}

						return {
							component: SettingsCellRenderer,
							params:    {
								...params,
								columnOrderIndex: 2,
							},
						};
					},
				},
				{
					field:           'impressions',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Impressions',
				},
				{
					field:           'listeners',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Listeners',
				},
				{
					field:           'adCompletes',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Ad Completes',
				},
				{
					field:           'clicks',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Clicks',
				},
				{
					field:           'conversionRate',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Conversion Rate',
				},
				{
					field:           'newListeners',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'New Listeners',
				},
				{
					field:           'playedTo25',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Played To 25%',
				},
				{
					field:           'playedTo50',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Played To 50%',
				},
				{
					field:           'playedTo75',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Played To 75%',
				},
				{
					field:           'playedTo100',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Played To 100%',
				},
				{
					field:           'starts',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Starts',
				},
				{
					field:           'streams',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Streams',
				},
				{
					field:           'streamsPerUser',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Streams Per User',
				},
				{
					field:           'unmutes',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Unmutes',
				},
				{
					field:           'videoViews',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Video Views',
				},
				{
					field:           'videoExpands',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Video Expands',
				},
				{
					field:           'skadAppInstalls',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'SKAD App Installs',
				},
				{
					field:           'kochavaAppInstalls',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Kochava App Installs',
				},
				{
					field:           'appsflyerAppInstalls',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'AppsFlyer App Installs',
				},
				{
					field:           'modeledAddToCart',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Modeled Add To Cart',
				},
				{
					field:           'modeledLeads',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Modeled Leads',
				},
				{
					field:           'modeledPageViews',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Modeled Page Views',
				},
				{
					field:           'modeledPurchases',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Modeled Purchases',
				},
				{
					field:           'unmodeledAddToCart',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Unmodeled Add To Cart',
				},
				{
					field:           'unmodeledLeads',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Unmodeled Leads',
				},
				{
					field:           'unmodeledPageViews',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Unmodeled Page Views',
				},
				{
					field:           'unmodeledPurchases',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Unmodeled Purchases',
				},
				{
					field:           'completionRate',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Completion Rate',
				},
				{
					field:           'ctr',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'CTR',
				},
				{
					field:              'eCpm',
					cellRenderer:       MoneyCellRenderer,
					suppressMovable:    true,
					headerName:         'eCPM',
					cellRendererParams: (params) => ({
						forcedCurrency: params.data.currency,
						placeholder:    '0',
					}),
				},
				{
					field:           'intentRate',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Intent Rate',
				},
				{
					field:           'listenerConversionRate',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Listener Conversion Rate',
				},
				{
					field:           'newListenerConversionRate',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'New Listener Conversion Rate',
				},
				{
					field:           'newListenerStreams',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'New Listener Streams',
				},
				{
					field:           'paidListens',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Paid Listens',
				},
				{
					field:           'paidListensReach',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Paid Listens Reach',
				},

				{
					field:           'videoExpandRate',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Video Expand Rate',
				},
				{
					field:           'frequency',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Frequency',
				},
				{
					field:           'frequencyOfAdCompletes',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Frequency Of Ad Completes',
				},
				{
					field:           'reach',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Reach',
				},
				{
					field:           'reachOfAdCompletes',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Reach Of Ad Completes',
				},
				{
					field:              'spend',
					cellRenderer:       MoneyCellRenderer,
					suppressMovable:    true,
					headerName:         'Spend',
					cellRendererParams: (params) => ({
						forcedCurrency: params.data.currency,
						placeholder:    '0',
					}),
				},
				{
					field:              'cpm',
					cellRenderer:       MoneyCellRenderer,
					suppressMovable:    true,
					headerName:         'CPM',
					cellRendererParams: (params) => ({
						forcedCurrency: params.data.currency,
						placeholder:    '0',
					}),
				},
				{
					field:              'cpcl',
					cellRenderer:       MoneyCellRenderer,
					suppressMovable:    true,
					headerName:         'CPCL',
					cellRendererParams: (params) => ({
						forcedCurrency: params.data.currency,
						placeholder:    '0',
					}),
				},
				{
					field:           'avgStreamsPerListener',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Avg Streams Per Listener',
				},
				{
					field:           'avgStreamsPerNewListener',
					cellRenderer:    StatCellRenderer,
					suppressMovable: true,
					headerName:      'Avg Streams Per New Listener',
				},
			],
		},
		levels: [
			{
				level:      SpotifyLevel.AD_ACCOUNT,
				color:      SpotifyColors.AD_ACCOUNT,
				nameColDef: {
					...NAME_COL_DEF,
					colId:      'adAccountName',
					headerName: 'Ad accounts',
				},
				anchors: {
					color:          'hsl(var(--blue-500))',
					secondaryColor: 'hsl(var(--partner-50))',
					firstColumnId:  'id',
					label:          'AD ACCOUNTS',
				},
				columns: [
					{
						field:    'id',
						rowGroup: true,
						hide:     true,
					},
				],
			},
			{
				level:      SpotifyLevel.CAMPAIGN,
				color:      SpotifyColors.CAMPAIGN,
				nameColDef: {
					...NAME_COL_DEF,
					colId:    'campaignName',
					editable: true,
				},
				anchors: {
					color:          'hsl(var(--campaign-600))',
					secondaryColor: 'hsl(var(--campaign-50))',
					label:          'CAMPAIGNS',
					subAnchors:     [{ colId: 'campaignObjective', label: 'Settings' }],
				},
				columns: [
					{
						field:        'id',
						columnMode:   ColumnModeTypeEnum.REGULAR,
						rowGroup:     true,
						hide:         true,
						elasticChild: 'settingsElastic',
					},
					{
						colId:              'campaignObjective',
						field:              'campaignObjective',
						headerName:         campaignHeaderNames.campaignObjective,
						columnMode:         ColumnModeTypeEnum.BOTH,
						editable:           (params) => params.data?.isInCreation,
						cellRenderer:       SelectCellRenderer,
						cellRendererParams: (params) => ({
							disabled: params?.data?.isCreate,
							options:  SPOTIFY_CAMPAIGN_OBJECTIVE_OPTIONS,
						}),
						cellEditor:       SelectCellEditor,
						cellEditorParams: {
							options: SPOTIFY_CAMPAIGN_OBJECTIVE_OPTIONS,
						},
						elasticChild: 'settingsElastic',
					},
				],
			},
			{
				level:      SpotifyLevel.GROUP,
				color:      SpotifyColors.GROUP,
				nameColDef: {
					...NAME_COL_DEF,
					colId:      'groupName',
					headerName: groupHeaderNames.groupName,
					editable:   editableOnLevel(SpotifyLevel.GROUP),
				},
				anchors: {
					color:          'hsl(var(--group-600))',
					secondaryColor: 'hsl(var(--group-50))',
					label:          'GROUPS',
					firstColumnId:  'groupTargets.groupTargetsPodcastIds',
					subAnchors:     [
						{ colId: 'groupTargets.groupTargetsPodcastIds', label: 'Ad set details' },
						{ colId: 'groupTargets.groupTargetsGeoTargets.countryCode', label: 'Audience' },
						{ colId: 'groupAssetFormat', label: 'Format and placement' },
						{ colId: 'groupBudget.type', label: 'Budget' },
						{ colId: 'groupStartTime', label: 'Schedule' },
						{ colId: 'groupFrequencyCapPerDay', label: 'Delivery' },
					],
				},
				columns: GROUP_COLUMN_DEFINITIONS.map((col) => ({
					...col,
					sortable: Object.values(GroupOrderByColumn).includes(col.field as GroupOrderByColumn),
				})),
			},
			{
				level:      SpotifyLevel.AD,
				color:      SpotifyColors.AD,
				nameColDef: {
					...NAME_COL_DEF,
					colId:       'adName',
					headerName:  adHeaderNames.adName,
					maxWidth:    36,
					headerClass: (params) => (params.context?.gridTopLevel === SpotifyLevel.AD
						? ['border-gray-300 border-b !overflow-visible border-l !w-auto']
						: ['border-gray-300 border-b !overflow-visible']),
					editable: editableOnLevel(SpotifyLevel.AD),
				},
				anchors: {
					color:          'hsl(var(--ad-600))',
					secondaryColor: 'hsl(var(--ad-50))',
					label:          'ADS',
					firstColumnId:  'adAdvertiserName',
					subAnchors:     [
						{ colId: 'adAdvertiserName', label: 'Ad details' },
						{ colId: 'adAsset', label: 'Visuals' },
					],
				},
				columns: AD_COLUMN_DEFINITIONS.map((col) => ({
					...col,
					sortable: Object.values(AdOrderByColumn).includes(col.field as AdOrderByColumn),
				})),
			},
			{
				level:   SpotifyLevel.BREAKDOWN,
				color:   'hsl(var(--gray-300))',
				anchors: {
					firstColumnId: 'status',
					label:         'BREAKDOWN',
					color:         'hsl(var(--gray-600))',
				},
				nameColDef: {
					...NAME_COL_DEF,
					colId:       'breakdownName',
					headerName:  'Name',
					editable:    false,
					minWidth:    0,
					width:       document.body.clientWidth / 4,
					maxWidth:    800,
					headerClass: 'border-gray-300 border-b border-r',
				},
				columns: [],
			},
		],
	})
	.getConfig();
